import React, { useState, useEffect } from "react";
import { Link, useLocation, NavLink } from "react-router-dom";
import { Menu, X, Globe, ChevronDown } from "lucide-react";
import { useI18n, useLocalizedPath } from "../i18n/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import { TID } from "../constants/testIds";

function LangToggle() {
  const { lang, setLang, langs } = useI18n();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const close = () => setOpen(false);
    if (open) window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [open]);

  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        data-testid={TID.langToggle}
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 text-xs font-mono-label text-white/70 hover:text-white transition-colors px-3 py-2 rounded-full border border-white/10 hover:border-white/25"
        aria-label="Language"
      >
        <Globe className="h-3.5 w-3.5" />
        <span>{lang.toUpperCase()}</span>
        <ChevronDown className="h-3 w-3" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 glass rounded-xl py-1 min-w-[120px] z-50">
          {langs.map((l) => (
            <button
              key={l}
              data-testid={TID.langOption(l)}
              onClick={() => {
                setLang(l);
                setOpen(false);
              }}
              className={`w-full text-left px-4 py-2 text-sm font-mono-label hover:bg-white/5 transition-colors ${
                l === lang ? "text-gold" : "text-white/70"
              }`}
            >
              {l === "en" ? "EN · English" : "RO · Română"}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const { t } = useI18n();
  const lp = useLocalizedPath();
  const { user, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const linkCls = ({ isActive }) =>
    `text-sm transition-colors ${isActive ? "text-white" : "text-white/60 hover:text-white"}`;

  const links = [
    { key: "guides", to: lp("/guides"), label: t.nav.guides },
    { key: "assessment", to: lp("/assessment"), label: t.nav.assessment },
    { key: "blog", to: lp("/blog"), label: t.nav.blog },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
        scrolled ? "py-3" : "py-5"
      }`}
    >
      <div className="container mx-auto px-6">
        <div
          className={`flex items-center justify-between transition-all duration-500 ${
            scrolled
              ? "glass rounded-full px-5 py-2.5"
              : "px-1 py-1"
          }`}
        >
          <Link
            to={lp("/")}
            data-testid={TID.navLogo}
            className="flex items-baseline gap-1 group"
          >
            <span className="font-display text-base font-bold tracking-tight">
              The Life
            </span>
            <span className="ed-italic text-base text-gold">Plot</span>
            <span className="ml-2 h-1 w-1 rounded-full bg-gold/70 self-center" />
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {links.map((l) => (
              <NavLink
                key={l.key}
                to={l.to}
                data-testid={TID.navLink(l.key)}
                className={linkCls}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <LangToggle />
            {user ? (
              <>
                <Link
                  to={lp("/dashboard")}
                  data-testid={TID.navDashboard}
                  className="text-sm text-white/70 hover:text-white transition-colors px-3 py-2"
                >
                  {t.nav.dashboard}
                </Link>
                <button
                  data-testid={TID.navLogout}
                  onClick={logout}
                  className="btn-ghost text-sm py-2 px-4"
                >
                  {t.nav.logout}
                </button>
              </>
            ) : (
              <>
                <Link
                  to={lp("/login")}
                  data-testid={TID.navLogin}
                  className="text-sm text-white/70 hover:text-white transition-colors px-3 py-2"
                >
                  {t.nav.login}
                </Link>
                <Link
                  to={lp("/register")}
                  data-testid={TID.navRegister}
                  className="btn-gold text-sm !py-2 !px-4"
                >
                  {t.nav.register}
                </Link>
              </>
            )}
          </div>

          <button
            className="md:hidden text-white/80"
            onClick={() => setMobileOpen((o) => !o)}
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden mt-3 glass rounded-2xl p-5 flex flex-col gap-4 animate-fade-up">
            {links.map((l) => (
              <Link
                key={l.key}
                to={l.to}
                data-testid={TID.navLink(`m-${l.key}`)}
                className="text-base text-white/80 hover:text-white"
              >
                {l.label}
              </Link>
            ))}
            <div className="divider-fade" />
            <div className="flex items-center justify-between">
              <LangToggle />
              {user ? (
                <button onClick={logout} className="btn-ghost text-sm">
                  {t.nav.logout}
                </button>
              ) : (
                <Link to={lp("/login")} className="btn-gold text-sm !py-2 !px-4">
                  {t.nav.login}
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
